import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../services/api';
import './NutritionChecklist.css';

function NutritionChecklist({ userId }) {
  const [checklist, setChecklist] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completionRate, setCompletionRate] = useState(0);
  const [expandedItems, setExpandedItems] = useState({});

  const loadChecklist = useCallback(async () => {
    setLoading(true);
    try {
      // Force English language
      const items = await api.getWeeklyChecklist(userId, 'en');
      setChecklist(items);
      
      // Calculate completion rate
      const completed = items.filter(item => item.completed).length;
      const rate = items.length > 0 ? Math.round((completed / items.length) * 100) : 0;
      setCompletionRate(rate);
    } catch (error) {
      console.error('Error loading checklist:', error);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadChecklist();
  }, [loadChecklist]);

  const handleToggle = async (itemKey, currentCompleted) => {
    if (!userId) return;
    
    const newCompleted = !currentCompleted;
    
    // Optimistically update UI
    setChecklist(prev => {
      const updated = prev.map(item => 
        item.itemKey === itemKey 
          ? { ...item, completed: newCompleted }
          : item
      );
      
      // Update completion rate
      const completedCount = updated.filter(item => item.completed).length;
      const rate = updated.length > 0 ? Math.round((completedCount / updated.length) * 100) : 0;
      setCompletionRate(rate);
      
      return updated;
    });
    
    // Call API
    try {
      await api.toggleChecklistItem(userId, itemKey, newCompleted);
    } catch (error) {
      console.error('Error toggling item:', error);
      // Revert on error
      loadChecklist();
    }
  };

  const toggleExpand = (itemKey, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    // Only toggle the specific item - ensure isolation
    setExpandedItems(prev => {
      const newState = { ...prev };
      // Explicitly toggle only this specific item
      newState[itemKey] = !Boolean(newState[itemKey]);
      return newState;
    });
  };

  // Enhanced checklist items with descriptions
  const getItemDetails = (itemKey) => {
    const details = {
      'checklist.item.protein': {
        icon: '🥩',
        description: 'Aim for 60-75g daily. Essential for your baby\'s growth and development. Found in lean meats, eggs, beans, and dairy.',
        dailyGoal: '60-75g daily',
        benefits: 'Supports baby\'s tissue growth, maintains your muscle mass, essential for brain development'
      },
      'checklist.item.iron': {
        icon: '⚡',
        description: 'Need 27mg daily to prevent anemia. Helps carry oxygen to your baby. Found in lean red meat, lentils, spinach, and fortified cereals.',
        dailyGoal: '27mg daily',
        benefits: 'Prevents anemia, supports oxygen transport, essential for baby\'s brain development'
      },
      'checklist.item.folate': {
        icon: '🌱',
        description: 'Critical 600mcg daily, especially in first trimester. Prevents neural tube defects. Found in dark leafy greens, lentils, and fortified foods.',
        dailyGoal: '600mcg daily',
        benefits: 'Prevents neural tube defects, supports DNA synthesis, essential for cell division'
      },
      'checklist.item.calcium': {
        icon: '🦴',
        description: '1000mg daily for your baby\'s bone development. Found in dairy products, fortified plant milks, dark leafy greens, and tofu.',
        dailyGoal: '1000mg daily',
        benefits: 'Builds baby\'s bones and teeth, maintains your bone density, supports muscle function'
      },
      'checklist.item.omega3': {
        icon: '🐟',
        description: '200-300mg DHA daily for baby\'s brain and eye development. Found in salmon (well-cooked), sardines, walnuts, and chia seeds.',
        dailyGoal: '200-300mg DHA daily',
        benefits: 'Supports baby\'s brain development, essential for eye development, may reduce preterm birth risk'
      },
      'checklist.item.vitaminD': {
        icon: '☀️',
        description: '600 IU daily for bone health and immune function. Found in fortified milk, fatty fish, egg yolks, and sunlight exposure.',
        dailyGoal: '600 IU daily',
        benefits: 'Supports baby\'s bone development, maintains your bone health, supports immune function'
      },
      'checklist.item.fiber': {
        icon: '🌾',
        description: '28-30g daily to prevent constipation. Found in whole grains, fruits, vegetables, legumes, and nuts.',
        dailyGoal: '28-30g daily',
        benefits: 'Prevents constipation, maintains healthy blood sugar, supports digestive health'
      },
      'checklist.item.vitaminC': {
        icon: '🍊',
        description: '85mg daily to support iron absorption and immune function. Found in citrus fruits, berries, bell peppers, and tomatoes.',
        dailyGoal: '85mg daily',
        benefits: 'Enhances iron absorption, supports immune function, promotes healthy skin'
      }
    };
    return details[itemKey] || { icon: '💊', description: 'Important nutrient for your pregnancy journey.', dailyGoal: 'As recommended', benefits: 'Supports your and your baby\'s health' };
  };

  if (loading) {
    return (
      <div className="nutrition-checklist-modern">
        <div className="checklist-loading-container">
          <div className="checklist-spinner"></div>
          <p className="loading-text">Loading your nutrition checklist...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="nutrition-checklist-modern">
      {/* SVG Gradient Definition */}
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <linearGradient id="completionGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#E91E63" />
            <stop offset="50%" stopColor="#EC407A" />
            <stop offset="100%" stopColor="#F48FB1" />
          </linearGradient>
        </defs>
      </svg>
      
      {/* Header with Progress */}
      <div className="checklist-header-enhanced">
        <div className="header-main">
          <motion.div
            className="header-icon-wrapper"
            animate={{ rotate: [0, -10, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, repeatDelay: 2 }}
          >
            <div className="header-icon-large">✅</div>
          </motion.div>
          <div className="header-text-content">
            <h2 className="checklist-title-enhanced">Weekly Nutrition Checklist</h2>
            <p className="checklist-subtitle-enhanced">
              Track your daily nutrition goals based on Mayo Clinic and ACOG recommendations
            </p>
          </div>
        </div>
        <motion.div
          className="completion-badge-enhanced"
          initial={{ scale: 0.9 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 300 }}
        >
          <div className="completion-circle">
            <svg className="completion-svg" viewBox="0 0 100 100">
              <circle
                className="completion-circle-bg"
                cx="50"
                cy="50"
                r="45"
              />
              <motion.circle
                className="completion-circle-fill"
                cx="50"
                cy="50"
                r="45"
                fill="none"
                strokeDasharray="283"
                initial={{ strokeDashoffset: 283 }}
                animate={{ strokeDashoffset: 283 - (283 * completionRate / 100) }}
                transition={{ duration: 1, ease: 'easeOut' }}
              />
            </svg>
            <div className="completion-percentage">{completionRate}%</div>
          </div>
          <div className="completion-label-enhanced">Complete</div>
        </motion.div>
      </div>

      {/* Progress Bar */}
      <div className="completion-progress-enhanced">
        <div className="progress-bar-wrapper">
          <motion.div
            className="progress-fill-enhanced"
            initial={{ width: 0 }}
            animate={{ width: `${completionRate}%` }}
            transition={{ duration: 1, ease: 'easeOut' }}
          />
          <div className="progress-text-overlay">
            {completionRate === 100 ? (
              <span className="progress-celebration">🎉 Amazing! You've completed all goals!</span>
            ) : (
              <span className="progress-message">
                {completionRate === 0 
                  ? 'Start tracking your nutrition goals' 
                  : `${checklist.filter(i => i.completed).length} of ${checklist.length} goals completed`}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Checklist Items */}
      <div className="checklist-items-enhanced">
        {checklist.length === 0 ? (
          <motion.div
            className="empty-checklist-enhanced"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="empty-icon-large">📋</div>
            <h3 className="empty-title">No checklist items available</h3>
            <p className="empty-text">Complete your pregnancy profile to see personalized nutrition goals.</p>
          </motion.div>
        ) : (
          <div className="checklist-grid">
            {checklist.map((item, index) => {
              const details = getItemDetails(item.itemKey);
              const isExpanded = expandedItems[item.itemKey];
              
              return (
                <ChecklistItemEnhanced
                  key={item.itemKey}
                  item={item}
                  details={details}
                  index={index}
                  isExpanded={isExpanded}
                  onToggle={handleToggle}
                  onExpand={toggleExpand}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function ChecklistItemEnhanced({ item, details, index, isExpanded, onToggle, onExpand }) {
  const itemKey = item.itemKey;
  const checkboxRef = useRef(null);
  const isCheckboxClick = useRef(false);
  
  // Match SmartDailySummary pattern exactly
  const handleExpandToggle = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    onExpand(itemKey, e);
  };

  const handleCheckboxClick = (e) => {
    // Mark that this is a checkbox click
    isCheckboxClick.current = true;
    
    // Completely stop event propagation
    e.preventDefault();
    e.stopPropagation();
    
    // Toggle the completion
    onToggle(itemKey, item.completed);
    
    // Reset flag after a short delay
    setTimeout(() => {
      isCheckboxClick.current = false;
    }, 100);
  };

  const handleHeaderClick = (e) => {
    // If this was a checkbox click, don't expand
    if (isCheckboxClick.current || 
        e.target === checkboxRef.current || 
        checkboxRef.current?.contains(e.target)) {
      return;
    }
    
    // Match SmartDailySummary pattern - direct state update
    handleExpandToggle(e);
  };

  return (
    <motion.div
      className={`checklist-item-enhanced ${item.completed ? 'completed' : ''} ${isExpanded ? 'expanded' : ''}`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.3 }}
      whileHover={!isExpanded ? { scale: 1.02, y: -4 } : {}}
    >
      <div 
        className="item-header-enhanced" 
        onClick={handleHeaderClick}
      >
        <div className="item-main-content">
          <div className="item-icon-wrapper">
            <span className="item-icon">{details.icon}</span>
          </div>
          <div className="item-text-content">
            <h4 className="item-title">{item.label}</h4>
            {!isExpanded && (
              <p className="item-preview">{details.dailyGoal}</p>
            )}
          </div>
        </div>
        <div className="item-actions">
          <div
            ref={checkboxRef}
            className="checkbox-wrapper-enhanced"
            onClick={handleCheckboxClick}
          >
            <input
              type="checkbox"
              className="checkbox-input-enhanced"
              checked={item.completed}
              readOnly
            />
            <span className={`checkbox-custom-enhanced ${item.completed ? 'checked' : ''}`}>
              {item.completed && <span className="checkmark">✓</span>}
            </span>
          </div>
          <button 
            className="expand-button-enhanced" 
            aria-label={isExpanded ? 'Collapse' : 'Expand'}
          >
            <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className="item-details-enhanced"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="details-content">
              <div className="detail-section">
                <div className="detail-label">
                  <span className="detail-icon">💡</span>
                  <span>Why It Matters</span>
                </div>
                <p className="detail-text">{details.description}</p>
              </div>
              <div className="detail-section">
                <div className="detail-label">
                  <span className="detail-icon">📊</span>
                  <span>Daily Goal</span>
                </div>
                <p className="detail-text highlight">{details.dailyGoal}</p>
              </div>
              <div className="detail-section">
                <div className="detail-label">
                  <span className="detail-icon">✨</span>
                  <span>Benefits</span>
                </div>
                <p className="detail-text">{details.benefits}</p>
              </div>
              <div className="detail-source">
                <strong>Source:</strong> Mayo Clinic Pregnancy Guide, ACOG Nutrition Guidelines
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default NutritionChecklist;
